import { ConfettiDark, ConfettiLight } from "@assets";
import {
  Image as CImage,
  Flex,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { CopyToClipboardIcon } from "@components/CopyToClipboard/CopyToClipboard";
import { NFTReward, NFTRewardType } from "@models/event/reward";
import { translate } from "@models/language";
import EventDetailAbout from "@providers/checkout/EventDetail/EventDetailAbout";
import Image from "next/image";
import CheckoutSuccessButtons from "../CheckoutSuccessButtons/CheckoutSuccessButtons";
import {
  ContactUsCard,
  CreateEventCard,
} from "../CheckoutSuccessCTA/CheckoutSuccessCTACards";
import CheckoutSuccessContainer from "../CheckoutSuccessContainer/CheckoutSuccessContainer";
import { CheckoutSuccessContentProps } from "../types";

const _t = (key?: string) => key || "";

const URLReward = ({ reward }: { reward: NFTReward }) => {
  return (
    <div className="flex flex-row justify-center items-center">
      <a
        href={reward.content}
        target="_blank"
        className="text-primary-500 text-xl underline"
      >
        {reward.content}
      </a>
      <CopyToClipboardIcon text={reward.content} />
    </div>
  );
};

const CodeReward = ({ reward }: { reward: NFTReward }) => {
  return (
    <div className="self-center flex flex-row items-center text-primary-500 text-xl">
      {reward.content}
      <CopyToClipboardIcon text={reward.content} />
    </div>
  );
};

const TextReward = ({ reward }: { reward: NFTReward }) => {
  return (
    // <div className="html-container">
    //   <div
    //     className="whitespace-pre-wrap font-medium text-black"
    //     dangerouslySetInnerHTML={safeHTML(reward.content)}
    //   ></div>
    // </div>
    <div className="flex flex-row justify-center items-center">
      <p className="w-full whitespace-pre-wrap font-medium text-primary-500 text-center text-xl">
        {reward.content}
      </p>
    </div>
  );
};

const ImageURLReward = ({ reward }: { reward: NFTReward }) => {
  return (
    <div className="flex flex-row justify-center items-center ">
      <CImage
        src={reward.content}
        alt={reward.id}
        className="max-w-lg overflow-hidden rounded"
      />
    </div>
  );
};

const RewardComponent = ({
  reward,
  alignText = "text-center",
}: {
  reward: NFTReward;
  alignText?: string;
}) => (
  <div
    className={`bg-primary-50 self-stretch text-neutral-700 p-4 rounded-lg whitespace-pre-line ${alignText} flex flex-col gap-4`}
  >
    {reward.instruction && (
      <p className="m-1 text-neutral-600 text-center px-8">
        {reward.instruction}
      </p>
    )}
    {reward.type == NFTRewardType.URL ? (
      <URLReward reward={reward} />
    ) : reward.type === NFTRewardType.IMAGE ? (
      <ImageURLReward reward={reward} />
    ) : reward.type === NFTRewardType.CODE ? (
      <CodeReward reward={reward} />
    ) : (
      <TextReward reward={reward} />
    )}
  </div>
);

export const BenefitContent = ({
  tier,
  info,
  language,
  reward,
  containerProps,
  alignText = "text-center",
}: CheckoutSuccessContentProps & {
  containerProps?: any;
  alignText?: string;
}) => (
  <Flex
    direction="column"
    justifyContent="center"
    alignItems="center"
    gap={6}
    width="100%"
    {...containerProps}
  >
    <p className={`text-2xl dark:text-white ${alignText}`}>
      {_t("Congrats! You have successfully redeem your benefit.")}
    </p>

    <div className="w-full">
      <p className={`text-2xl text-primary-500 ${alignText}`}>{info?.name}</p>

      <p className={`text-lg text-primary-500 mt-2 ${alignText}`}>
        {translate(tier?.info, language).name}
      </p>

      {info.descriptions.find((desc) => desc.title === "about") && (
        <EventDetailAbout
          title=""
          content={
            info.descriptions.find((desc) => desc.title === "about")?.content ||
            ""
          }
          containerClassName={`w-full mt-2 ${alignText}`}
        />
      )}
    </div>

    {reward && <RewardComponent reward={reward} alignText={alignText} />}
  </Flex>
);

const CheckoutSuccessBenefit = (props: CheckoutSuccessContentProps) => (
  <CheckoutSuccessContainer>
    <Flex
      direction="column"
      justifyContent="center"
      alignItems="center"
      gap={4}
      width="100%"
      p={8}
    >
      <Flex
        alignSelf="flex-start"
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap={2}
      >
        <Image
          src={useColorModeValue(ConfettiLight, ConfettiDark)}
          alt="confetti"
          width={50}
          height={50}
        />
        <Text
          fontWeight="600"
          className="text-2xl font-medium text-primary-500 dark:text-white"
        >
          {_t("Purchase Confirmed!")}
        </Text>
      </Flex>

      <div className="bg-white dark:bg-[rgba(255,255,255,0.1)] px-8 rounded-xl border border-neutral-60 dark:border-0">
        <BenefitContent
          {...props}
          containerProps={{
            alignItems: "flex-start",
            mb: 4,
            mt: 4,
          }}
          alignText="text-left"
        />
        {/* <CheckoutSuccessReferral
          codes={["https://moongate.id/redeemable"]}
          codeTransform="lowercase"
          subtitle={"Your link to access the offer:"}
          showCopyIcon={true}
        /> */}
        <CheckoutSuccessButtons
          redirectToAppText="See my Benefits"
          type={props.event.type}
          btnStyle="fit-content"
        />
      </div>

      <Flex
        width="full"
        direction={{ base: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        gap={4}
      >
        <CreateEventCard language={props.language} />
        <ContactUsCard language={props.language} />
      </Flex>
    </Flex>
  </CheckoutSuccessContainer>
);

export default CheckoutSuccessBenefit;
